/**
 * Shared utility functions
 */
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs) {
    return twMerge(clsx(inputs));
}
/**
 * Generate a unique operation ID for tracing
 */
export function generateOperationId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}
/**
 * Generate a unique document ID
 */
export function generateDocumentId() {
    return `doc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}
/**
 * Sleep for a specified duration
 */
export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Check if a string is a valid IP address
 */
export function isValidIp(ip) {
    // IPv4
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (ipv4Regex.test(ip)) {
        const parts = ip.split('.').map(Number);
        return parts.every((part) => part >= 0 && part <= 255);
    }
    // IPv6 (simplified check)
    const ipv6Regex = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv6Regex.test(ip);
}
/**
 * Check if an IPv4 address falls within a CIDR range. Non-IPv4 inputs and
 * malformed CIDR strings return false (fail-closed).
 */
export function isIpInRange(ip, cidr) {
    const slash = cidr.indexOf('/');
    if (slash === -1)
        return false;
    const range = cidr.slice(0, slash);
    const bitsStr = cidr.slice(slash + 1);
    if (!/^\d+$/.test(bitsStr))
        return false;
    const bits = Number(bitsStr);
    if (bits < 0 || bits > 32)
        return false;
    const ipNum = ipv4ToNumber(ip);
    const rangeNum = ipv4ToNumber(range);
    if (ipNum === null || rangeNum === null)
        return false;
    if (bits === 0)
        return true;
    const mask = ~((1 << (32 - bits)) - 1) >>> 0;
    return (ipNum & mask) === (rangeNum & mask);
}
function ipv4ToNumber(ip) {
    const parts = ip.split('.');
    if (parts.length !== 4)
        return null;
    let n = 0;
    for (const part of parts) {
        if (!/^\d+$/.test(part))
            return null;
        const v = Number(part);
        if (v < 0 || v > 255)
            return null;
        n = (n << 8) | v;
    }
    return n >>> 0;
}
/**
 * Check if email matches a domain pattern (supports wildcards)
 * Example: *.muni.cz matches student@fi.muni.cz
 */
export function emailMatchesDomain(email, pattern) {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain)
        return false;
    const normalizedPattern = pattern.toLowerCase();
    // Handle wildcard patterns like *.muni.cz
    if (normalizedPattern.startsWith('*.')) {
        const suffix = normalizedPattern.slice(1); // .muni.cz
        return domain.endsWith(suffix) || domain === normalizedPattern.slice(2);
    }
    return domain === normalizedPattern;
}
/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes) {
    if (bytes === 0)
        return '0 B';
    if (bytes < 0)
        return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
/**
 * Debounce function
 */
export function debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}
/**
 * Throttle function
 */
export function throttle(fn, limit) {
    let inThrottle = false;
    return (...args) => {
        if (!inThrottle) {
            fn(...args);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
            }, limit);
        }
    };
}
//# sourceMappingURL=utils.js.map